import { Variant } from "@/types/index";
import { isValidNewSkiPassPackages } from "./isValidNewSkiPassPackages";

/**
 * Groups the given array of variants by their `prestIdFusion`.
 *
 * @param {Variant[]} variants - The array of variants to group.
 * @returns {Variant[][]} - An array of grouped variants. Each inner array contains all the variants that share the same `prestIdFusion`.
 */
const groupVariantsByPrestIdFusion = (variants: Variant[]): Variant[][] => {
  const grouped: Record<string, Variant[]> = {};

  variants.forEach(variant => {
    const prestIdFusion: string = variant.prestIdFusion;
    if (!grouped[prestIdFusion]) {
      grouped[prestIdFusion] = [];
    }
    grouped[prestIdFusion].push(variant);
  });

  return Object.values(grouped);
};

/**
 * Sorts variants by skipass duration and price within each duration group.
 * Variants with the same skipass duration will be grouped together and sorted by price.
 * 
 * @param {Variant[]} variants - The array of variants to sort
 * @returns {Variant[]} - Sorted array of variants
 */
export const sortVariantsBySkipassAndPrice = (variants: Variant[]): Variant[] => {
  if (!variants.length) return [];

  // Group variants by skipass duration
  const groupedBySkipass = variants.reduce((acc, variant) => {
    const skipass = variant.skipass || 0;
    if (!acc[skipass]) {
      acc[skipass] = [];
    }
    acc[skipass].push(variant);
    return acc;
  }, {} as Record<number, Variant[]>);

  // Sort variants within each group by price
  Object.keys(groupedBySkipass).forEach(skipass => {
    groupedBySkipass[Number(skipass)].sort((a, b) => a.pricePerLogement - b.pricePerLogement);
  });

  // Get all skipass durations and sort them
  const skipassDurations = Object.keys(groupedBySkipass).map(Number).sort((a, b) => a - b);

  // Flatten the groups in order of skipass duration
  return skipassDurations.reduce((acc, skipass) => {
    return [...acc, ...groupedBySkipass[skipass]];
  }, [] as Variant[]);
};

/**
 * Updates each variant in the grouped variants with the validity status of New SkiPass packages combo (PF_7 with PF_6 OR PFP_7 with PFP_6).
 * 
 * @param {Array<Array<Object>>} groupedVariants - An array of groups, where each group is an array of variant objects.
 * @returns {Array<Object>} A flattened array of updated variant objects, each including the `isNewSkiPassValid` property.
 */
const addNewSkiPassFlag = (groupedVariants: Variant[][] ): Variant[][]  => {
	return groupedVariants.map(group => {
		const isNewSkiPassValid: boolean = isValidNewSkiPassPackages(group);
    const updatedGroupd = group.map(variant => ({ ...variant, isNewSkiPassValid }));
		return updatedGroupd
	});
}

/**
 * Groups variants by their `prestIdFusion`, filters out groups with less than two variants, and updates each variant
 * with the validity status of New SkiPass packages.
 * 
 * This function performs the following steps:
 * 1. Groups the input variants by their `prestIdFusion` property, filtering out groups with less than two variants.
 * 2. Checks if each group of variants meets the criteria for New SkiPass packages.
 * 3. Adds an `isNewSkiPassValid` property to each variant, indicating the validity status of the New SkiPass packages.
 * 4. Flattens the grouped and updated variants into a single array.
 * 
 * @param {Array<Variant>} variants - An array of variant objects, where each object should contain the `prestIdFusion` property.
 * @returns {Array<Variant>} An array of variant objects with an additional `isNewSkiPassValid` property. Each object in the returned array is a variant from the input, updated to include the validity status of the New SkiPass packages.
 * 
 * @see {@link groupVariantsByPrestIdFusion} for grouping logic.
 * @see {@link addNewSkiPassFlag} for the logic to update the validity status.
 */
export const updateVariants = (variants: Variant[]): Variant[] => {
  if(!variants.length || !Array.isArray(variants)) return []
  const groupedVariants:Variant[][] = groupVariantsByPrestIdFusion(variants);
  const updatedVariants: Variant[] = addNewSkiPassFlag(groupedVariants).flat();
  return sortVariantsBySkipassAndPrice(updatedVariants);
};